import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import type { PickingInfo } from '@deck.gl/core';
import type { GeoJsonFeature } from '../utils/ogc';
import SensorDetailContent from '../components/detail/SensorDetailsPage';
import { useSensorLocations } from '../layers/sensor_locations';
import useTimeRange from '../hooks/useTimeRange';

export const Route = createFileRoute('/sensor/$sensorId')({
  component: SensorDetailPage,
});

function SensorDetailPage() {
  const router = useRouter();
  const { sensorId } = Route.useParams();
  const { features, loading, error } = useSensorLocations();

  const handleClose = React.useCallback(() => {
    // Prefer going back in history; fall back to home
    if (window.history.length > 1) {
      router.history.back();
    } else {
      router.navigate({ to: '/' });
    }
  }, [router]);

  const { timeRange, currentTime, setTimeRange } = useTimeRange();

  const feature = React.useMemo(() => {
    if (!features) return undefined;
    return features.find((f) => {
      const p = f.properties as any;
      const id = p.deployment_id || p.id || f.id;
      return String(id) === sensorId;
    });
  }, [features, sensorId]);

  const info = React.useMemo(() => {
    if (!feature) return null;
    return {
      object: feature as GeoJsonFeature,
      index: 0,
      picked: true,
      x: 0,
      y: 0,
      layer: null as any,
    } as PickingInfo<GeoJsonFeature>;
  }, [feature]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !info) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sensor not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We couldn&apos;t find a sensor with id &ldquo;{sensorId}&rdquo;.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', justifyContent: 'center', p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1200,
          bgcolor: 'background.paper',
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: { xs: 'none', sm: 6 },
          overflow: 'hidden',
        }}
      >
        <SensorDetailContent
          info={info}
          timeRange={timeRange}
          currentTime={currentTime}
          onClose={handleClose}
          onSelectRange={setTimeRange}
        />
      </Box>
    </Box>
  );
}
